import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    faHourglassEnd,
    faClock,
    faCar,
    faDollarCircle,
    faCheckSquare,
    faUsers,
    faArrowUpRight,
    faPiggyBank,
} from '@fortawesome/pro-regular-svg-icons';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { cashCollectionsMessages } from 'src/features/cashCollections/utils/messages';
import { statisticsMessages } from 'src/features/statistics/utils';
import useHasPermissions from 'src/hooks/useHasPermissions';
import {
    GeneralBlockHead,
    GeneralBlockHeader,
    GeneralBlockSubtitle,
    GeneralBlockTitle,
} from 'src/registry/components/blocks/GeneralBlock';
import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock.tsx';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';

const SectionLink = ({
    icon,
    title,
    subtitle,
    to,
}: {
    icon: IconProp;
    title: string;
    subtitle: string;
    to: string;
}) => {
    return (
        <Link
            className="flex h-auto gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left font-normal hover:bg-gray-100 hover:fill-gray-900 hover:text-gray-900 dark:border-gray-800 dark:bg-gray-950 dark:hover:bg-gray-800 dark:hover:text-gray-50"
            to={to}
        >
            <GeneralBlockHeader>
                <SquareIcon icon={icon} />
                <GeneralBlockHead>
                    <GeneralBlockTitle className="text-base text-gray-900">{title}</GeneralBlockTitle>
                    <GeneralBlockSubtitle>{subtitle}</GeneralBlockSubtitle>
                </GeneralBlockHead>
            </GeneralBlockHeader>
        </Link>
    );
};

function Reports() {
    const { formatMessage } = useIntl();
    const permissionVisits = useHasPermissions([PERMISSION.STATS_VISITS]);
    const permissionDurations = useHasPermissions([PERMISSION.STATS_DURATIONS]);
    const permissionTimeframe = useHasPermissions([PERMISSION.STATS_TIMEFRAME]);
    const permissionPayments = useHasPermissions([PERMISSION.STATS_PAYMENTS]);
    const permissionValidations = useHasPermissions([PERMISSION.STATS_VALIDATIONS]);
    const permissionGuestActions = useHasPermissions([PERMISSION.STATS_GUEST_ACTIONS]);
    const permissionManualActions = useHasPermissions([PERMISSION.STATS_MANUAL_ACTIONS]);
    const permissionStatsCashCollections = useHasPermissions([PERMISSION.STATS_CASH_COLLECTIONS]);
    return (
        <>
            {permissionVisits && (
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockHead>
                            <SectionBlockTitle>{formatMessage(statisticsMessages.visits)}</SectionBlockTitle>
                            <SectionBlockSubtitle className="max-w-fit">
                                {formatMessage(statisticsMessages.visitsSubtitle)}
                            </SectionBlockSubtitle>
                        </SectionBlockHead>
                    </SectionBlockHeader>
                    <SectionBlockSeparator />
                    <SectionBlockBody className="grid md:grid-cols-3">
                        <SectionLink
                            to="parking-visits"
                            icon={faCar}
                            title={formatMessage(statisticsMessages.allVisits)}
                            subtitle={formatMessage(statisticsMessages.allVisitsSubtitle)}
                        />
                    </SectionBlockBody>
                </SectionBlock>
            )}
            {(permissionDurations || permissionTimeframe) && (
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockHead>
                            <SectionBlockTitle>
                                {formatMessage(statisticsMessages.occupancyStatistics)}
                            </SectionBlockTitle>
                            <SectionBlockSubtitle className="max-w-fit">
                                {formatMessage(statisticsMessages.occupancyStatisticsSubtitle)}
                            </SectionBlockSubtitle>
                        </SectionBlockHead>
                    </SectionBlockHeader>
                    <SectionBlockSeparator />
                    <SectionBlockBody className="grid md:grid-cols-3">
                        {permissionDurations && (
                            <SectionLink
                                to="occupancy-duration"
                                icon={faHourglassEnd}
                                title={formatMessage(statisticsMessages.duration)}
                                subtitle={formatMessage(statisticsMessages.durationSubtitle)}
                            />
                        )}
                        {permissionTimeframe && (
                            <SectionLink
                                to="occupancy-timeframe"
                                icon={faClock}
                                title={formatMessage(statisticsMessages.timeFrame)}
                                subtitle={formatMessage(statisticsMessages.timeFrameSubtitle)}
                            />
                        )}
                    </SectionBlockBody>
                </SectionBlock>
            )}
            {permissionPayments && (
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockHead>
                            <SectionBlockTitle>{formatMessage(statisticsMessages.payments)}</SectionBlockTitle>
                            <SectionBlockSubtitle className="max-w-fit">
                                {formatMessage(statisticsMessages.paymentsSubtitle)}
                            </SectionBlockSubtitle>
                        </SectionBlockHead>
                    </SectionBlockHeader>
                    <SectionBlockSeparator />
                    <SectionBlockBody className="grid md:grid-cols-3">
                        <SectionLink
                            to="payments"
                            icon={faDollarCircle}
                            title={formatMessage(statisticsMessages.allPayments)}
                            subtitle={formatMessage(statisticsMessages.allPaymentsSubtitle)}
                        />
                        {permissionStatsCashCollections && (
                            <SectionLink
                                to="cash-collections"
                                icon={faPiggyBank}
                                title={formatMessage(cashCollectionsMessages.allCashCollections)}
                                subtitle={formatMessage(cashCollectionsMessages.cashCollectionsReport)}
                            />
                        )}
                    </SectionBlockBody>
                </SectionBlock>
            )}
            <SectionBlock>
                <SectionBlockHeader>
                    <SectionBlockHead>
                        <SectionBlockTitle>{formatMessage(statisticsMessages.operations)}</SectionBlockTitle>
                        <SectionBlockSubtitle className="max-w-fit">
                            {formatMessage(statisticsMessages.operationsSubtitle)}
                        </SectionBlockSubtitle>
                    </SectionBlockHead>
                </SectionBlockHeader>
                <SectionBlockSeparator />
                <SectionBlockBody className="grid md:grid-cols-3">
                    {permissionValidations && (
                        <SectionLink
                            to="operation-validations"
                            icon={faCheckSquare}
                            title={formatMessage(statisticsMessages.allValidations)}
                            subtitle={formatMessage(statisticsMessages.allValidationsSubtitle)}
                        />
                    )}
                    {permissionGuestActions && (
                        <SectionLink
                            to="operation-guests"
                            icon={faUsers}
                            title={formatMessage(statisticsMessages.makeGuestActions)}
                            subtitle={formatMessage(statisticsMessages.makeGuestActionsSubtitle)}
                        />
                    )}
                    {permissionManualActions && (
                        <SectionLink
                            to="operation-manual-point-opens"
                            icon={faArrowUpRight}
                            title={formatMessage(statisticsMessages.manualPointActions)}
                            subtitle={formatMessage(statisticsMessages.manualPointActionsSubtitle)}
                        />
                    )}
                </SectionBlockBody>
            </SectionBlock>
        </>
    );
}

export default Reports;
